import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userFetch } from '../lib/userFetch';
import '../styles/tienda.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://giroexpress-ruleta-backend.onrender.com';

// Mostrar el botón de compra mock SOLO cuando se habilita explícitamente
// vía env (desarrollo). En producción queda oculto.
const SHOW_MOCK_BUTTON = process.env.REACT_APP_SHOW_MOCK_PURCHASE === 'true';

const formatCop = (n) => {
  const num = Number(n) || 0;
  return `$${num.toLocaleString('es-CO')} COP`;
};

const Tienda = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Modal premium de compra aprobada (post-pago MP).
  // Lleva las recompensas reales otorgadas + totales actuales del usuario.
  const [successModal, setSuccessModal] = useState(null);

  // Si el modal está abierto sin totales y user.uid llega después (AuthContext
  // suele resolver async), reintentar el fetch.
  useEffect(() => {
    if (!successModal || successModal.totals || !user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await userFetch(`${API_URL}/api/user/${user.uid}`);
        const d = await r.json();
        if (cancelled) return;
        const u = (r.ok && d?.success && d.user) ? d.user : null;
        console.log('[MP-SUCCESS-MODAL] userData (retry)', u);
        if (!u) return;
        setSuccessModal((m) => (m ? { ...m, totals: {
          normalTickets: Number(u.normalTickets || 0),
          goldenTickets: Number(u.goldenTickets || 0),
          points:        Number(u.points || u.xp || 0),
        } } : m));
      } catch (_) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [successModal, user?.uid]);

  // Mostrar toast / modal según ?status= devuelto por Mercado Pago en back_urls.
  // Se ejecuta una sola vez al montar / cambiar la query y limpia el query string.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = (params.get('status') || '').toLowerCase();
    if (!status) return;

    // Camino premium para success: leemos totales reales + paquete persistido
    if (status === 'success') {
      const purchaseIdFromUrl = params.get('purchase') || '';

      // ----- LOGS DE DIAGNÓSTICO (NO TOCAR) -----
      console.log('[MP-LOAD] purchase param', purchaseIdFromUrl);
      console.log('[MP-LOAD] session', sessionStorage.getItem('mp_pending_pkg'));
      console.log('[MP-LOAD] last', localStorage.getItem('mp_last_pending_pkg'));
      console.log('[MP-LOAD] by purchase', purchaseIdFromUrl ? localStorage.getItem(`mp_pkg_${purchaseIdFromUrl}`) : '(no purchase param)');
      console.log('[MP-LOAD] all localStorage keys', Object.keys(localStorage));
      // ------------------------------------------

      let pending = null;
      // 1) Buscar primero por purchaseId concreto en localStorage (cross-tab)
      try {
        if (purchaseIdFromUrl) {
          const raw = localStorage.getItem(`mp_pkg_${purchaseIdFromUrl}`);
          if (raw) pending = JSON.parse(raw);
        }
        // 2) Fallback: sessionStorage (misma pestaña)
        if (!pending) {
          pending = JSON.parse(sessionStorage.getItem('mp_pending_pkg') || 'null');
        }
        // 3) Fallback: último paquete pendiente guardado (cualquier pestaña)
        if (!pending) {
          pending = JSON.parse(localStorage.getItem('mp_last_pending_pkg') || 'null');
        }
      } catch (_) { pending = null; }

      console.log('[MP-SUCCESS-MODAL] pendingPkg', pending);

      // NOTA: la limpieza del storage se hace al cerrar el modal (botón "¡Genial!"),
      // NO acá. React puede re-invocar este effect (StrictMode, cambio de auth, etc.)
      // y necesitamos que el storage persista para que el segundo render no quede vacío.

      // Render inmediato del modal con la info del paquete (sin esperar fetch)
      const reward = {
        normalTickets: Number(pending?.tickets_normales || 0),
        goldenTickets: Number(pending?.tickets_dorados || 0),
        xp:            Number(pending?.xp_por_compra   || 0),
      };
      setSuccessModal({
        reward,
        totals: null, // ← null marca "Actualizando total..."
        packageName: pending?.nombre || '',
      });

      // En paralelo: traer totales reales del usuario.
      // Si user.uid aún no está disponible, dejamos totals en null y otro
      // effect lo intentará cuando el AuthContext termine de cargar.
      (async () => {
        if (!user?.uid) {
          console.log('[MP-SUCCESS-MODAL] userData null (user no listo, se reintentará)');
          return;
        }
        let user_now = null;
        try {
          const r = await userFetch(`${API_URL}/api/user/${user.uid}`);
          const d = await r.json();
          if (r.ok && d?.success && d.user) user_now = d.user;
        } catch (_) { /* noop */ }
        console.log('[MP-SUCCESS-MODAL] userData', user_now);
        if (!user_now) return; // mantener totals=null → "Actualizando total…"
        const totals = {
          normalTickets: Number(user_now.normalTickets || 0),
          goldenTickets: Number(user_now.goldenTickets || 0),
          points:        Number(user_now.points || user_now.xp || 0),
        };
        setSuccessModal((m) => (m ? { ...m, totals } : m));
      })();
    } else {
      // pending / failure → toast simple como hoy
      const map = {
        pending: { type: 'xp',    title: '⏳ Pago pendiente', sub: 'Te avisaremos cuando se confirme.' },
        failure: { type: 'error', title: '❌ Pago cancelado', sub: 'No se realizó el cobro.' },
      };
      const conf = map[status];
      if (conf) {
        setFeedback(conf);
        window.setTimeout(() => setFeedback(null), 4000);
      }
    }
    // Limpiar query para que al recargar/volver no reaparezca
    navigate(location.pathname, { replace: true });
  }, [location.search]);

  // Config remota (panel admin → Mongo tienda_config/_id=default)
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [globals, setGlobals] = useState({});
  const [paquetes, setPaquetes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    console.log('[TIENDA-APP] cargando config');
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/tienda/config`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data?.ok || !data?.config) {
          throw new Error('Respuesta inválida');
        }
        const cfg = data.config;
        const list = Array.isArray(cfg.paquetes) ? cfg.paquetes : [];
        setGlobals(cfg.globals || {});
        setPaquetes(list);
        const visibles = [];
        if (cfg.globals?.ventas_normales_activas) visibles.push('normal');
        if (cfg.globals?.ventas_dorados_activas) visibles.push('dorado');
        if (cfg.globals?.ventas_mixtos_activas) visibles.push('mixto');
        console.log('[TIENDA-APP] paquetes recibidos=' + list.length);
        console.log('[TIENDA-APP] categorias visibles ' + visibles.join('/'));
      } catch (err) {
        if (cancelled) return;
        console.log('[TIENDA-APP] error carga config', err?.message || err);
        setConfigError(true);
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const grupos = useMemo(() => {
    // Backend ya ordena, pero defendemos por si acaso.
    const sorted = [...paquetes].sort((a, b) => (a.orden ?? 9999) - (b.orden ?? 9999));
    return {
      normal: sorted.filter((p) => p.categoria === 'normal'),
      dorado: sorted.filter((p) => p.categoria === 'dorado'),
      mixto: sorted.filter((p) => p.categoria === 'mixto'),
    };
  }, [paquetes]);

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
  };

  const showFeedback = (payload) => {
    setFeedback(payload);
    window.setTimeout(() => setFeedback(null), 3200);
  };

  // Checkout real con Mercado Pago (paralelo al mock — NO reemplaza el mock).
  // Pide al backend que cree una Preference y redirige al init_point de MP.
  // La acreditación de tickets/XP ocurrirá tras el webhook firmado (próximo paso).
  const handleMercadoPagoCheckout = async () => {
    if (!selectedPackage || purchasing) return;
    if (!user?.uid) {
      showFeedback({
        type: 'error',
        title: 'Sesión requerida',
        sub: 'Inicia sesión para continuar al pago',
      });
      return;
    }
    setPurchasing(true);
    try {
      const res = await userFetch(`${API_URL}/api/payments/mercadopago/preference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          packageId: selectedPackage.id,
        }),
      });
      // Leer body UNA sola vez. Reutilizar `data` en todos los checks.
      let data = null;
      try {
        data = await res.json();
      } catch (_) {
        data = null;
      }
      if (!res.ok || !data?.success || !data?.init_point) {
        const errMsg =
          data?.detail?.message ||
          data?.detail?.error ||
          data?.error ||
          `No se pudo iniciar el pago (HTTP ${res.status})`;
        throw new Error(errMsg);
      }
      // Persistir el paquete para mostrar las recompensas reales al volver.
      console.log('[MP-SAVE] selectedPackage', selectedPackage);
      console.log('[MP-SAVE] purchaseId', data.purchaseId);
      try {
        const payload = {
          id: selectedPackage.id,
          nombre: selectedPackage.nombre,
          tickets_normales: Number(selectedPackage.tickets_normales) || 0,
          tickets_dorados: Number(selectedPackage.tickets_dorados) || 0,
          xp_por_compra: Number(selectedPackage.xp_por_compra) || 0,
          purchaseId: data.purchaseId,
        };
        const raw = JSON.stringify(payload);
        sessionStorage.setItem('mp_pending_pkg', raw);
        localStorage.setItem('mp_last_pending_pkg', raw);
        if (data.purchaseId) {
          localStorage.setItem(`mp_pkg_${data.purchaseId}`, raw);
        }
        console.log('[MP-SAVE] stored payload', payload);
        console.log('[MP-SAVE] readback session:', sessionStorage.getItem('mp_pending_pkg'));
        console.log('[MP-SAVE] readback last:', localStorage.getItem('mp_last_pending_pkg'));
        console.log('[MP-SAVE] readback by purchase:', data.purchaseId ? localStorage.getItem(`mp_pkg_${data.purchaseId}`) : '(no purchaseId)');
      } catch (e) {
        console.log('[MP-SAVE] error', e?.message || e);
      }
      // Redirigir al checkout de Mercado Pago
      window.location.href = data.init_point;
    } catch (err) {
      showFeedback({
        type: 'error',
        title: 'No se pudo iniciar el pago',
        sub: err?.message || 'Inténtalo de nuevo',
      });
      setPurchasing(false);
    }
  };

  const handleConfirmMockPurchase = async () => {
    if (!selectedPackage || purchasing) return;
    if (!user?.uid) {
      showFeedback({
        type: 'error',
        title: 'Sesión requerida',
        sub: 'Inicia sesión para registrar la compra',
      });
      return;
    }

    setPurchasing(true);
    try {
      const res = await fetch(`${API_URL}/api/ticket-purchases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          packageId: selectedPackage.id,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.detail?.message || data?.error || 'No se pudo registrar la compra');
      }
      setSelectedPackage(null);
      showFeedback({
        type: 'success',
        title: 'Compra mock registrada',
        sub: 'Tus tiquetes fueron agregados en modo prueba',
      });
      // Feedback de XP si la compra otorgó XP (≥1)
      const xpAwarded = Number(data?.xpAwarded || 0);
      if (xpAwarded > 0) {
        const pointsAfter = Number(data?.user?.points || 0);
        // Difundir el evento para que la tarjeta de XP (Ruleta) se refresque
        // si está montada en paralelo (no rompe nada si nadie escucha).
        try {
          window.dispatchEvent(new CustomEvent('xp:credited', {
            detail: { xpAwarded, pointsAfter, origen: 'compra_tiquetes' },
          }));
        } catch (_) { /* noop */ }
        // Toast secundario diferido (se solapa al de éxito tras 600ms)
        window.setTimeout(() => {
          showFeedback({
            type: 'xp',
            title: `⚡ +${xpAwarded} XP agregados`,
            sub: '🎯 Te acercaste a tu próximo canje',
          });
        }, 600);
      }
    } catch (err) {
      showFeedback({
        type: 'error',
        title: 'No se pudo registrar la compra',
        sub: err?.message || 'Inténtalo de nuevo',
      });
    } finally {
      setPurchasing(false);
    }
  };

  const renderBadge = (pkg) => {
    if (!pkg.badge_texto) return null;
    const isPopular = /m[áa]s elegido/i.test(pkg.badge_texto);
    return (
      <div className={`badge ${isPopular ? 'popular' : 'recommended'}`}>
        {!isPopular && '★ '}{pkg.badge_texto}
      </div>
    );
  };

  const renderPackageCard = (pkg, variant) => {
    const isGolden = variant === 'golden';
    const isMixed = variant === 'mixed';
    const icon = isGolden ? '⭐' : isMixed ? '🎁' : '🎫';
    const cardClass = `ticket-card${isGolden ? ' golden' : ''}${isMixed ? ' mixed' : ''}`;
    const btnClass = `btn-select${isGolden ? ' golden' : ''}`;
    const cta = pkg.cta_texto || '🎁 Obtener paquete';
    return (
      <div key={pkg.id} className={cardClass}>
        {renderBadge(pkg)}
        <div className="card-icon">{icon}</div>
        <div className="card-title">{(pkg.nombre || '').toUpperCase()}</div>
        <div className="card-subtitle">{pkg.subtitulo || ''}</div>
        <div className="card-price">{formatCop(pkg.precio_cop)}</div>
        {Number(pkg.xp_por_compra) > 0 && (
          <div className="card-xp-bonus" data-testid={`pkg-xp-bonus-${pkg.id}`}>
            <span className="card-xp-bonus__bolt">⚡</span>
            <span className="card-xp-bonus__text">
              +{Number(pkg.xp_por_compra)} XP al comprar
            </span>
          </div>
        )}
        <button
          className={btnClass}
          onClick={() => handleSelectPackage(pkg)}
          data-testid={`pkg-select-${pkg.id}`}
        >
          {cta}
        </button>
      </div>
    );
  };

  const showNormales = globals.ventas_normales_activas !== false;
  const showDorados = globals.ventas_dorados_activas !== false;
  const showMixtos = globals.ventas_mixtos_activas !== false;

  return (
    <div className="tienda-container">
      {/* Back Button */}
      <button className="back-button" onClick={() => navigate('/')} data-testid="tienda-back-btn">
        ←
      </button>

      {/* Header */}
      <div className="tienda-header">
        <div className="header-title">
          <span className="header-icon">🎫</span>
          <span>Tienda de Tiquetes</span>
        </div>
        <p className="header-subtitle">🔥 Elige tu estrategia</p>
      </div>

      {loadingConfig && (
        <div className="tienda-empty" data-testid="tienda-loading">
          Cargando paquetes…
        </div>
      )}

      {!loadingConfig && configError && (
        <div className="tienda-empty" data-testid="tienda-error">
          No pudimos cargar la tienda. Intenta nuevamente.
        </div>
      )}

      {!loadingConfig && !configError && (
        <>
          {/* Tiquetes Normales */}
          {showNormales && grupos.normal.length > 0 && (
            <section className="section">
              <h2 className="section-title">
                <span className="section-icon">🎫</span>
                TIQUETES NORMALES (24h)
              </h2>
              <div className="cards-grid">
                {grupos.normal.map((pkg) => renderPackageCard(pkg, 'normal'))}
              </div>
            </section>
          )}

          {/* Tiquetes Dorados */}
          {showDorados && grupos.dorado.length > 0 && (
            <section className="section">
              <h2 className="section-title">
                <span className="section-icon">⭐</span>
                TIQUETES DORADOS (NO EXPIRAN)
              </h2>
              <div className="cards-grid">
                {grupos.dorado.map((pkg) => renderPackageCard(pkg, 'golden'))}
              </div>
            </section>
          )}

          {/* Paquetes Mixtos */}
          {showMixtos && grupos.mixto.length > 0 && (
            <section className="section">
              <h2 className="section-title">
                <span className="section-icon">🔥</span>
                PAQUETES MIXTOS 🔥
              </h2>
              <div className="cards-grid">
                {grupos.mixto.map((pkg) => renderPackageCard(pkg, 'mixed'))}
              </div>
            </section>
          )}

          {/* Bottom CTA */}
          <div className="bottom-cta">
            <p>Juega hoy y guarda oportunidades para después</p>
          </div>
        </>
      )}

      {/* Package Selection Modal */}
      {selectedPackage && (
        <div className="modal-overlay" onClick={() => !purchasing && setSelectedPackage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Botón cerrar (X) esquina superior derecha */}
            <button
              type="button"
              className="modal-close-x"
              onClick={() => setSelectedPackage(null)}
              disabled={purchasing}
              aria-label="Cerrar"
              data-testid="pkg-modal-close-x"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
            <h3>Confirmar selección</h3>
            <div className="modal-package-info">
              <p className="modal-package-name">{selectedPackage.nombre}</p>
              <p className="modal-package-desc">{selectedPackage.subtitulo}</p>
              <div className="modal-package-details">
                {Number(selectedPackage.tickets_normales) > 0 && (
                  <span>🎫 {selectedPackage.tickets_normales} tiquetes normales (24h)</span>
                )}
                {Number(selectedPackage.tickets_dorados) > 0 && (
                  <span>⭐ {selectedPackage.tickets_dorados} tiquetes dorados</span>
                )}
              </div>
              <p className="modal-package-price">{formatCop(selectedPackage.precio_cop)}</p>
            </div>
            <div className="modal-buttons modal-buttons--single">
              <button
                className="btn-continue"
                onClick={handleMercadoPagoCheckout}
                disabled={purchasing}
                data-testid="pkg-modal-pay-mp"
              >
                {purchasing ? 'Redirigiendo…' : '🔥 Activar y girar'}
              </button>
            </div>
            {SHOW_MOCK_BUTTON && (
              <button
                className="btn-mock-dev"
                onClick={handleConfirmMockPurchase}
                disabled={purchasing}
                data-testid="pkg-modal-confirm-mock"
                title="Solo visible cuando REACT_APP_SHOW_MOCK_PURCHASE=true (desarrollo)"
              >
                {purchasing ? 'Registrando…' : 'Compra mock (dev)'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal premium de compra aprobada (post-pago Mercado Pago) */}
      {successModal && (
        <div
          className="success-overlay"
          onClick={() => {
            try {
              const params = new URLSearchParams(location.search);
              const pid = params.get('purchase') || '';
              sessionStorage.removeItem('mp_pending_pkg');
              localStorage.removeItem('mp_last_pending_pkg');
              if (pid) localStorage.removeItem(`mp_pkg_${pid}`);
            } catch (_) { /* noop */ }
            setSuccessModal(null);
          }}
          data-testid="mp-success-overlay"
        >
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            {/* Confeti CSS (2s) */}
            <div className="success-confetti" aria-hidden="true">
              {Array.from({ length: 18 }).map((_, i) => (
                <span key={i} className={`confetti-piece confetti-${(i % 6) + 1}`}>
                  {['⚡','💎','🎉','⭐','🎟','🔥'][i % 6]}
                </span>
              ))}
            </div>
            <div className="success-check" data-testid="mp-success-check">
              <div className="success-check-glow"></div>
              <svg viewBox="0 0 64 64" className="success-check-svg">
                <circle cx="32" cy="32" r="28" className="success-check-circle"/>
                <path d="M18 33 L28 43 L46 23" className="success-check-tick"/>
              </svg>
            </div>
            <h2 className="success-title">🎉 ¡Pago aprobado!</h2>
            <p className="success-subtitle">Tu compra fue exitosa 🚀</p>

            <div className="success-rewards">
              {successModal.reward.normalTickets > 0 && (
                <div className="reward-card reward-card--ticket" data-testid="reward-normal">
                  <div className="reward-card__icon">🎟</div>
                  <div className="reward-card__body">
                    <div className="reward-card__main">
                      +{successModal.reward.normalTickets} Tiquetes normales
                    </div>
                    <div className="reward-card__sub">
                      {successModal.totals
                        ? `Ahora tienes: ${successModal.totals.normalTickets} disponibles`
                        : 'Actualizando total…'}
                    </div>
                  </div>
                </div>
              )}
              {successModal.reward.goldenTickets > 0 && (
                <div className="reward-card reward-card--golden" data-testid="reward-golden">
                  <div className="reward-card__icon">🎟</div>
                  <div className="reward-card__body">
                    <div className="reward-card__main">
                      +{successModal.reward.goldenTickets} Tiquetes dorados
                    </div>
                    <div className="reward-card__sub">
                      {successModal.totals
                        ? `Ahora tienes: ${successModal.totals.goldenTickets} disponibles`
                        : 'Actualizando total…'}
                    </div>
                  </div>
                </div>
              )}
              {successModal.reward.xp > 0 && (
                <div className="reward-card reward-card--xp" data-testid="reward-xp">
                  <div className="reward-card__icon">⚡</div>
                  <div className="reward-card__body">
                    <div className="reward-card__main">
                      +{successModal.reward.xp} XP obtenidos
                    </div>
                    <div className="reward-card__sub">
                      {successModal.totals
                        ? `Tu total ahora es: ${successModal.totals.points} XP`
                        : 'Actualizando total…'}
                    </div>
                  </div>
                </div>
              )}
              {/* Si pendingPkg no traía recompensas: NO mostrar fallback de texto;
                 dejar el bloque vacío para no contradecir la realidad. */}
            </div>

            <button
              className="success-cta"
              onClick={() => {
                // Limpieza diferida del storage SOLO al cerrar el modal
                try {
                  const params = new URLSearchParams(location.search);
                  const pid = params.get('purchase') || '';
                  sessionStorage.removeItem('mp_pending_pkg');
                  localStorage.removeItem('mp_last_pending_pkg');
                  if (pid) localStorage.removeItem(`mp_pkg_${pid}`);
                } catch (_) { /* noop */ }
                setSuccessModal(null);
              }}
              data-testid="mp-success-cta"
            >
              🔥 ¡Genial!
            </button>
            <p className="success-foot">Gracias por participar 💛</p>
          </div>
        </div>
      )}

      {/* Feedback Toast */}
      {feedback && (
        <div className={`tienda-toast tienda-toast-${feedback.type}`} data-testid="tienda-toast">
          <div className="tienda-toast-title">{feedback.title}</div>
          <div className="tienda-toast-sub">{feedback.sub}</div>
        </div>
      )}
    </div>
  );
};

export default Tienda;
